import Tabs from "./tabs";
import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Stack, Typography, Avatar, Button, Tooltip, Dialog, DialogContent } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyIcon from '@mui/icons-material/Key';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { setChatBotDetails } from "../chatBotSlice";
import ChatBotClickHistoryModal from "./botClickHistoryModal";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../config/appRoutes";
import { QR, qrDialogPaperSx, QrModalHeader } from "../../qrCode/components/qrUi";
import { ChatBotCreateForm } from "./chatBotCreation";

export default function ChatBotList() {

    const { getChatBotDetails, status, error } = useSelector((state) => state.chatBot);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [state, setState] = useState({
        viewBotHistory: false,
        rowData: []
    });

    const [createOpen, setCreateOpen] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const columns = useMemo(() => [
        {
            header: "SNO",
            accessorKey: "sno",
            enableGlobalFilter: true,
            size: 70,
            Cell: ({ row }) => <Typography variant="body2" color="text.secondary">{row.original.sno}</Typography>,
        },
        {
            accessorKey: "chat_bot_name", header: "Bot Name",
            Cell: ({ row }) => (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    title="View click history"
                    onClick={() => setState(prev => ({ ...prev, viewBotHistory: true, rowData: row.original }))}
                    sx={{
                        cursor: "pointer",
                        // clickable: always blue + underlined (darker on hover)
                        "& .bot-name": { color: "#2563eb !important", textDecoration: "underline", textDecorationColor: "#2563eb66", textUnderlineOffset: "3px" },
                        "&:hover .bot-name": { color: "#1d4ed8 !important", textDecorationColor: "#1d4ed8" },
                        "&:hover .bot-icon": { bgcolor: QR.blue, color: "#fff" },
                    }}
                >
                    <Avatar className="bot-icon" variant="rounded" sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: `${QR.main}14`, color: QR.main, transition: "all .15s ease" }}>
                        <SmartToyIcon sx={{ fontSize: 17 }} />
                    </Avatar>
                    <Typography className="bot-name" variant="body2" fontWeight={700} color="#0f172a" noWrap>
                        {row.original.chat_bot_name}
                    </Typography>
                </Stack>
            )
        },
        {
            accessorKey: "key_words",
            header: "Key Word",
            Cell: ({ row }) =>
                row.original.key_words ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 1.5, fontFamily: "monospace", fontSize: 12.5, fontWeight: 700, color: QR.purple, bgcolor: `${QR.purple}12`, border: `1px solid ${QR.purple}26` }}>
                        <KeyIcon sx={{ fontSize: 13 }} />
                        {row.original.key_words}
                    </Box>
                ) : "-",
        },
        {
            accessorKey: "inserted_time",
            header: "Inserted Time",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">{formatDate(row.original.inserted_time)}</Typography>
            ),
        }
    ], []);

    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [error]);

    // Summary cards (display only, from the loaded bots)
    const bots = Array.isArray(getChatBotDetails) ? getChatBotDetails : [];
    const now = new Date();
    const thisMonth = bots.filter((b) => {
        const d = new Date(b.inserted_time);
        return !Number.isNaN(d.getTime()) && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    const latest = bots.reduce((acc, b) => {
        const t = new Date(b.inserted_time).getTime();
        return !Number.isNaN(t) && (!acc || t > acc.t) ? { t, name: b.chat_bot_name } : acc;
    }, null);
    const stats = [
        { label: "Total Bots", value: bots.length, icon: <SmartToyIcon />, color: QR.main },
        { label: "Added This Month", value: thisMonth, icon: <EventAvailableIcon />, color: QR.blue },
        { label: "Latest Bot", value: latest?.name || "-", icon: <NewReleasesOutlinedIcon />, color: QR.amber, small: true },
    ];

    return (
        <div>
            <Tabs stats={stats} onCreate={() => setCreateOpen(true)}>
                <DataTableWithCheckbox
                    status={status}
                    columns={columns}
                    data={getChatBotDetails || []}
                    enableRowSelection={false}
                    searchFirst
                    emptyMessage="No chat bots yet. Create one to get started."
                    renderRowActions={({ row }) => {
                        const { key_words, id, chat_bot_name } = row.original;

                        return (
                            <Tooltip title="Edit the bot flow" arrow>
                                <Button
                                    size="small"
                                    startIcon={<EditIcon sx={{ fontSize: "16px !important" }} />}
                                    onClick={() => {
                                        dispatch(setChatBotDetails([{
                                            ...row.original,
                                            id: id,
                                            chatBotName: chat_bot_name,
                                            chatBotKey: key_words
                                        }]));
                                        navigate(APP_ROUTES.CHATBOT.CHATBOT_FLOW);
                                    }}
                                    sx={{
                                        height: 30,
                                        px: 1.25,
                                        borderRadius: 2,
                                        textTransform: "none",
                                        fontSize: 12.5,
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                        color: QR.blue,
                                        bgcolor: `${QR.blue}0f`,
                                        border: `1px solid ${QR.blue}26`,
                                        "&:hover": { bgcolor: QR.blue, color: "#fff", borderColor: QR.blue },
                                    }}
                                >
                                    Edit Flow
                                </Button>
                            </Tooltip>
                        )
                    }}
                />
            </Tabs>

            {/* Create bot popup */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: { sm: 600 } } }}
            >
                <QrModalHeader
                    icon={<SmartToyIcon />}
                    title="Create Chat Bot"
                    subtitle="Name your bot and choose its keyword"
                    onClose={() => setCreateOpen(false)}
                />
                <DialogContent sx={{ p: 0 }}>
                    {/* remounts on each open, so the form starts empty */}
                    {createOpen && <ChatBotCreateForm embedded onCancel={() => setCreateOpen(false)} />}
                </DialogContent>
            </Dialog>

            <ChatBotClickHistoryModal
                show={state.viewBotHistory}
                handleClose={() => setState({ viewBotHistory: false })}
                rowData={state.rowData || {}}
            />
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </div>
    );
}
